import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { ProvidentFundService } from '../../services/providentFund.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { IMyPf } from '../../interfaces/providentFund.interface';
import { SummaryStats, money } from './provisionShared';
import { LedgerTable } from './PfMembersTab';

// W3-J: employee self-service "My Provident Fund". The API scopes everything to the caller.
const MyProvidentFund: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();
    const institutionId = selectedInstitutionId || AuthService.getSelectedInstitution() || '';
    const [data, setData] = useState<IMyPf | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            setData(await ProvidentFundService.mine(institutionId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load your provident fund');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { load(); }, [load]);

    const download = async () => {
        setDownloading(true);
        try {
            await ProvidentFundService.downloadMyStatement(institutionId);
        } catch (e) {
            toast.showApiError(e, 'Failed to download the statement');
        } finally {
            setDownloading(false);
        }
    };

    const member = data?.ledger?.member;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="h5">My Provident Fund</Typography>
                {member && <Button variant="contained" onClick={download} disabled={downloading}>Download statement (PDF)</Button>}
            </Box>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
            {!loading && data && !data.enabled && (
                <Alert severity="info">Provident fund is not set up for your organisation.</Alert>
            )}
            {!loading && data && data.enabled && !data.ledger && (
                <Alert severity="info">You are not enrolled in the provident fund.</Alert>
            )}
            {!loading && data?.enabled && data.ledger && member && (
                <>
                    <SummaryStats items={[
                        { label: 'Total balance', value: money(member.totalBalance) },
                        { label: 'Your contributions', value: money(member.employeeBalance) },
                        { label: 'Employer contributions', value: money(member.employerBalance) },
                        { label: 'Profit credited', value: money(member.profitBalance) },
                        { label: 'Vested', value: `${member.vestedPercent}%` },
                        { label: 'Withdrawable', value: money(member.withdrawable) },
                    ]} />
                    <LedgerTable entries={data.ledger.entries} pageKey="my-pf-ledger" />
                </>
            )}
        </Box>
    );
};

export default MyProvidentFund;
